import { Menu, Transition } from "@headlessui/react";
import { DotsVerticalIcon } from "@heroicons/react/outline";
import { find, isEmpty, orderBy } from "lodash";
import moment from "moment";
import React, { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import ContactPaymentActionsButton from "../../../components/ContactPaymentActionsButton";
import { useCommissionReports } from "../../../context/commissionReports";
import { getCommissionReportTitle } from "../../../context/commissionReports/CommissionReportsProvider";
import { getPaymentContextTitle } from "../../../context/contacts/ContactsProvider";
import { useMembers } from "../../../context/members";
import classNames from "../../../utils/classNames";
import { Entity } from "../../../utils/constants";
import {
  EXPORT_DATETIME_FORMAT,
  EXPORT_DATE_FORMAT,
  formatDateToLocale
} from "../../../utils/dates";
import { ContactPayment, PaymentTransaction } from "../../../utils/types";
import useCurrency, { CurrencyKey } from "../../../utils/useCurrency";
import { useHntMarket } from "../../../utils/useHntMarket";
import AddContactPaymentFormSidebar from "./AddContactPaymentFormSidebar";
import ContactPaymentSidebar from "./ContactPaymentSidebar";
import EntityDataTable from "./EntityDataTable";
import MemberCell from "./MemberCell";
import PaymentTransactionSidebar from "./PaymentTransactionSidebar";
import TablePropertiesSidebar from "./TablePropertiesSidebar";
import { Property } from "./useDataTable";
import { PaginationKey } from "./usePagination";

interface Props {
  className?: string;
  paginationKey: PaginationKey;
  title: string;
  contactPayments: ContactPayment[];
  loading?: boolean;
  contactUuid: string;
}

const ContactPaymentsDataTable: React.FC<Props> = ({
  className,
  paginationKey,
  title,
  contactPayments,
  loading = false,
  contactUuid
}) => {
  const { getCurrencyTitle, currencyDisplay } = useCurrency();
  const { displayInCurrency } = useHntMarket();
  const [showPropertiesSidebar, setShowPropertiesSidebar] = useState(false);
  const { members } = useMembers();
  const [isContactPaymentSidebarOpen, setIsContactPaymentSidebarOpen] =
    useState(false);
  const [isTransactionSidebarOpen, setIsTransactionSidebarOpen] =
    useState(false);
  const [currentTransaction, setCurrentTransaction] =
    useState<PaymentTransaction | null>(null);
  const [currentContactPayment, setCurrentContactPayment] =
    useState<ContactPayment | null>(null);
  const { commissionReports } = useCommissionReports();
  const [
    openAddContactPaymentFormSidebar,
    setOpenAddContactPaymentFormSidebar
  ] = useState(false);

  const toggleAddContactPaymentFormSidebar = () =>
    setOpenAddContactPaymentFormSidebar((prevValue) => !prevValue);

  const properties: { [key: string]: Property<ContactPayment> } = useMemo(
    () => ({
      created_at: {
        key: "created_at",
        title: "Added by",
        style: { minWidth: "13rem" },
        renderForExport: (contactPayment) => {
          const member = find(members, { uuid: contactPayment.created_by });

          if (!member) return null;

          return `${member?.name} - ${moment(contactPayment.created_at)
            .utc()
            .format(EXPORT_DATETIME_FORMAT)}`;
        },
        renderContent: (contactPayment) => (
          <MemberCell
            memberUuid={contactPayment.created_by}
            subtitle={formatDateToLocale(contactPayment.created_at)}
          />
        )
      },
      paid_at: {
        key: "paid_at",
        title: "Payment date",
        style: { minWidth: "10rem" },
        renderForExport: (contactPayment) => {
          const value = contactPayment.paid_at;

          if (!value) return null;

          return moment(value).utc().format(EXPORT_DATE_FORMAT);
        },
        renderContent: (contactPayment) =>
          contactPayment.paid_at && formatDateToLocale(contactPayment.paid_at)
      },
      amount: {
        key: "amount",
        title: "Amount",
        style: { minWidth: "10rem" },
        renderContent: (contactPayment) =>
          contactPayment.currency === CurrencyKey.hnt
            ? displayInCurrency(contactPayment.amount, {
                withHntSuffix: true,
                needsDivision: true,
                minimumFractionDigits: 3,
                maximumFractionDigits: 3
              })
            : currencyDisplay(contactPayment.currency, contactPayment.amount)
      },
      currency: {
        key: "currency",
        title: "Currency",
        renderContent: (contactPayment) =>
          getCurrencyTitle(contactPayment.currency)
      },
      context: {
        key: "context",
        title: "Context",
        renderForExport: (contactPayment) => contactPayment.context,
        renderContent: (contactPayment) => {
          // TODO: Add a function to get the context title

          if (!contactPayment.report_commission_uuid) {
            return getPaymentContextTitle(contactPayment.context);
          }

          return (
            <>
              <div>{getPaymentContextTitle(contactPayment.context)}</div>
              <Link
                className="text-sm hover:text-gray-600 truncate font-semibold dark:hover:text-white dark:hover:underline"
                to={`/workspace/commission-reports/${contactPayment.report_commission_uuid}`}
              >
                {commissionReports[contactPayment.report_commission_uuid]
                  ? getCommissionReportTitle(
                      commissionReports[contactPayment.report_commission_uuid]
                    )
                  : "Commission report"}
              </Link>
            </>
          );
        }
      },
      notes: {
        key: "notes",
        title: "Notes",
        style: { minWidth: "20rem" },
        renderForExport: (contactPayment) => contactPayment.notes,
        renderContent: (contactPayment) =>
          contactPayment.notes ? (
            <pre className="text-sm">{contactPayment.notes}</pre>
          ) : null
      },
      open: {
        key: "open",
        title: "Details",
        renderContent: (contactPayment) => (
          <button
            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
            onClick={() => {
              setCurrentContactPayment(contactPayment);
              setIsContactPaymentSidebarOpen(true);
            }}
          >
            Open
          </button>
        )
      },
      actions: {
        key: "actions",
        title: "Actions",
        renderContent: (contactPayment) => (
          <ContactPaymentActionsButton contactPayment={contactPayment} />
        )
      }
    }),
    [
      commissionReports,
      currencyDisplay,
      displayInCurrency,
      getCurrencyTitle,
      members
    ]
  );

  const sortedContactPayments = useMemo(() => {
    return orderBy(
      contactPayments,
      [(contactPayment) => moment(contactPayment.created_at).valueOf()],
      ["desc"]
    );
  }, [contactPayments]);

  const renderPaymentsTable = useMemo(() => {
    if (isEmpty(sortedContactPayments) && loading) {
      return <CenteredLoading className="h-96" />;
    }

    return (
      <EntityDataTable<ContactPayment>
        paginationKey={paginationKey}
        entity={Entity.contactPayment}
        items={sortedContactPayments}
        properties={properties}
      />
    );
  }, [loading, paginationKey, properties, sortedContactPayments]);

  return (
    <>
      <AddContactPaymentFormSidebar
        contactUuid={contactUuid}
        open={openAddContactPaymentFormSidebar}
        toggleContactPaymentForm={toggleAddContactPaymentFormSidebar}
      />

      <TablePropertiesSidebar<ContactPayment>
        entity={Entity.contactPayment}
        properties={properties}
        open={showPropertiesSidebar}
        setOpen={setShowPropertiesSidebar}
      />

      <div className="flex items-center justify-between px-4 pb-4">
        <div className="flex-1">{title}</div>

        <div className="flex items-center justify-end">
          <Menu as="div" className="relative">
            {({ open }) => (
              <>
                <div>
                  <Menu.Button className="p-2 rounded-full bg-white flex items-center text-gray-400 hover:text-gray-600 focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white dark:hover:bg-gray-700">
                    <span className="sr-only">Open options</span>
                    <DotsVerticalIcon className="h-5 w-5" aria-hidden="true" />
                  </Menu.Button>
                </div>

                <Transition
                  show={open}
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items
                    static
                    className="z-50 origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800"
                  >
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => {
                              setShowPropertiesSidebar(true);
                            }}
                            className={classNames(
                              active
                                ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white"
                                : "text-gray-700 dark:bg-gray-800 dark:text-white",
                              "w-full flex justify-between px-4 py-2 text-sm"
                            )}
                          >
                            <span>Properties</span>
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </>
            )}
          </Menu>

          {!loading && (
            <button
              type="button"
              className="ml-4 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
              onClick={toggleAddContactPaymentFormSidebar}
            >
              Register payment
            </button>
          )}
        </div>
      </div>

      {renderPaymentsTable}

      <PaymentTransactionSidebar
        transaction={currentTransaction}
        open={isTransactionSidebarOpen}
        onClose={() => {
          setIsTransactionSidebarOpen(false);
          setCurrentTransaction(null);
        }}
      />

      <ContactPaymentSidebar
        contactPayment={currentContactPayment}
        open={isContactPaymentSidebarOpen}
        onClose={() => {
          setIsContactPaymentSidebarOpen(false);
          setCurrentContactPayment(null);
        }}
      />
    </>
  );
};

export default ContactPaymentsDataTable;
